import {AgentData} from '@amplify/athena-models';
import API from '../../../APIs/API';
import Utils from '../../../Core/Utils';
import {AgeComputedCalendar, AgeComputedRounded} from '../../../Core/helpers/computed/Age';
import {Parameter, ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Quote} from '../../../Core/mobx/ParameterStore/Quote';
import {quoteGuards} from '../../../Core/mobx/ParameterStore/QuoteGuards';
import {fieldApi, fieldOptions} from '../../../Core/mobx/ParameterStore/QuoteXref';
import RootStore from '../../../Core/mobx/RootStore';
import {TemplateDataFields} from '../../ContactForm/EmailEditor/EmailEditor';
import {COMPANY_NAME, WRITING_AGENT_NAME} from '../constants';
import {OutListItem} from './types';

function getValuesForWinflexApi(c: string, quote: Quote): string {
    if (c.includes('+')) {
        const [name, num] = c.split('+');
        return (+(quote[name as keyof typeof quote] as Parameter).value + +num).toString();
    }
    if (c.includes('-')) {
        const [name, num] = c.split('-');
        return (+(quote[name as keyof typeof quote] as Parameter).value - +num).toString();
    }
    const out = quote.hasOwnProperty(c) ? (quote[c as keyof typeof quote] as Parameter).value : c;
    return out.toString();
}

export const fgWinflexAPI = async (
    parameterStore: ParameterStore,
    customerName: string,
    agentSession: AgentData | null,
    formType?: 'single-quote' | 'combination-quote',
) => {
    let quote = parameterStore.quote;
    if (formType === 'combination-quote' && parameterStore.quoteCombination) {
        quote = parameterStore.quoteCombination;
    }
    const productIUL = Utils.MakeVarName(quote.productIUL.value);
    const bday = parameterStore.approval.bday.value;
    const bmonth = parameterStore.approval.bmonth.value;
    const byear = parameterStore.approval.byear.value;
    const {managerName = '', managerLicenseNumber = ''} = agentSession || {};

    const age = AgeComputedCalendar(+bday, +bmonth, +byear);

    const nargs = Object.entries(quote)
        .map(([n, p]) => {
            if (quoteGuards.hasOwnProperty(n) && !quoteGuards[n](parameterStore, quote)) {
                return;
            }

            const field = fieldApi.hasOwnProperty(n) ? fieldApi[n as keyof typeof fieldApi] : null;

            if (field && 'api' in field && field.api != null) {
                const a = (field.api as Array<{api: string; name: string; value?: any}>).find(
                    (a: any) => a.api == 'winflex',
                );
                if (a == null) {
                    return;
                }

                const ret: {name: string; value: any; type?: string} = {name: a.name, value: null, type: field.type};
                const fieldOption: {options: string[]; optionValues?: string[]} | null = fieldOptions.hasOwnProperty(n)
                    ? fieldOptions[n as keyof typeof fieldOptions]
                    : null;

                if (a.hasOwnProperty('value') && a['value' as keyof typeof a] != null) {
                    const aVal = a['value' as keyof typeof a];
                    ret.value = JSON.parse(JSON.stringify(aVal)).map((x: any) => {
                        if (x.start == 'age') x.start = age;
                        else x.start = getValuesForWinflexApi(x.start, quote);
                        x.start = x.start - age + 1;

                        x.end = getValuesForWinflexApi(x.end, quote);
                        x.end = x.end - age + 1;

                        x.answer = getValuesForWinflexApi(x.answer, quote);
                        if (fieldOption != null) {
                            const options = fieldOption.optionValues ? fieldOption.optionValues : fieldOption.options;
                            const index = fieldOption.options.findIndex((o) => o == x.answer);
                            if (index > -1) {
                                x.answer = options[index];
                            } else {
                                console.log('could not find index', x.answer, fieldOption.options);
                            }
                        }
                        return x;
                    });
                } else {
                    ret.value = p.value;
                    if (fieldOption != null) {
                        const options = fieldOption.optionValues ? fieldOption.optionValues : fieldOption.options;
                        const index = fieldOption.options.findIndex((o) => Utils.MakeVarName(o) == ret.value);
                        if (index > -1) {
                            ret.value = options[index];
                        } else {
                            console.log('could not find index', ret.value, fieldOption.options);
                        }
                    }

                    if (field.type == 'number' || field.type == 'currency') {
                        if (ret.value == null) ret.value = 0;
                        ret.value = +ret.value;
                    }

                    if (field.type == 'boolean') {
                        if (ret.value == null) p.value = false;
                        ret.value = ret.value ? 'Y' : 'N';
                    }

                    // For some reason, LSW's PolicySolveFor option value for 'No Solve' is N
                    // Whereas Amerita's implementation is C. So, override the value as needed
                    // Double check Ameritas' documentation
                    if (
                        ret.name === 'Policy.SolveFor' &&
                        ret.value === 'C' &&
                        productIUL !== 'AmeritasIULFLXlivingbenefits' &&
                        productIUL !== 'MOOIULExpress'
                    ) {
                        ret.value = 'N';
                    }

                    // For specifiedPremiumEndAgeFGPathsetter, append @ in front to specify age
                    if (ret.name === 'Policy.PremiumYears' && ['FGPathsetter', 'FGEverlast'].includes(productIUL)) {
                        ret.value = '@' + ret.value;
                    }
                }

                console.log('ret is', ret);

                return ret;
            }
        })
        .filter((x) => x != null);

    const names = new Map<string, {name: string; value: any; type?: string}>();
    nargs.forEach((x) => {
        if (x == null) {
            return;
        }

        // always take the numeric param over other params
        if (x.type == 'number' || !names.has(x.name)) {
            names.set(x.name, x);
        }
    });

    const args = Array.from(names.values());

    const gender = parameterStore.approval.gender.value || parameterStore.prequal.gender.value;
    const state = parameterStore.approval.state.value;

    args.push({name: 'Agent.Company', value: COMPANY_NAME});
    args.push({name: 'Insured.Name', value: customerName});
    args.push({name: 'Insured.Sex', value: gender == 'Male' ? 'M' : 'F'});
    args.push({name: 'Insured.State', value: state});
    if (parameterStore.prequal.age.value < 1) {
        args.push({
            name: 'Insured.Age',
            value: parameterStore.prequal.age.value,
        });
    } else {
        args.push({
            name: 'Insured.DateOfBirth',
            value: ('0' + bmonth).slice(-2) + '/' + ('0' + bday).slice(-2) + '/' + byear,
        });
    }
    args.push({name: 'Agent.Addr1', value: '2261 Market Street #5482'});
    args.push({name: 'Agent.City', value: 'San Francisco'});
    args.push({name: 'Agent.State', value: 'CA'});
    args.push({name: 'Agent.Zip', value: '94114'});
    args.push({name: 'Agent.Phone', value: '855-687-4300'});

    let company = 'FGL';
    let product = 'STANCAUL2';

    if (productIUL == 'FGEverlast') {
        product = 'STANCAUL';
    }

    args.push({name: 'Index.MorStanUSEquWBon.Pct', value: '0'});
    args.push({name: 'Index.BMAdvAnnWBon.Pct', value: '0'});
    args.push({name: 'Index.SP500.AnnPTP100.Pct', value: '100'});
    args.push({name: 'Policy.PremiumMode', value: 'M'});
    args.push({
        name: 'Agent.Name',
        value: managerName || WRITING_AGENT_NAME,
    });
    if (managerLicenseNumber) {
        args.push({name: 'Agent.Lic', value: managerLicenseNumber});
    }
    // Default values from https://files.slack.com/files-pri/TLSKYBND6-F044B4CRP0E/screen_shot_2022-09-29_at_1.08.02_pm.png

    const premium = args.find((a) => a.name === 'Policy.Premium')?.value;

    const initialResp = await API.Call<any>('v2/fg-winflex', 'POST', {company, product, data: args});
    if (!initialResp || !initialResp.data || !initialResp.data.requestId) {
        return {error: 'Failed to initiate request'};
    }

    const requestId = initialResp.data.requestId;
    let pollResp = null;
    let attempts = 0;
    const maxAttempts = 30;
    const pollInterval = 10000; // 10 seconds

    while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        pollResp = (await API.Call(`v2/fg-winflex/${requestId}`, 'GET')) as any;
        if (pollResp && pollResp.data && pollResp.data.status !== 'pending' && pollResp.data.status !== 'processing') {
            break;
        }
        attempts++;
    }

    if (!pollResp || !pollResp.data || pollResp.data.status !== 'completed') {
        return {error: 'Request timed out'};
    }

    const resp = pollResp.data.response;
    if (resp == null) {
        return {error: 'time-out please try again in 20 seconds'};
    }

    const changes = resp.changes || [];
    const out = resp?.ProposalInfoOut?.Client?.[0] || {};

    if (out.Message != null) {
        if (out.Message[0].$.level !== 'WARNING') {
            return {error: out.Message[0].Text[0], req: JSON.stringify(args), changes};
        }
    }

    if (out.Keyword != null) {
        const outParams: Array<{
            id: string;
            label: string;
            type: 'year' | 'currency' | 'string';
        }> = [
            {id: 'CompanyName', label: 'Carrier', type: 'string'},
            {id: 'Policy', label: 'Product name', type: 'string'},
            {id: 'RiskClass', label: 'Risk Class', type: 'string'},
            {id: 'InitialAnnPrem', label: 'Initial annual premium', type: 'currency'},
            {id: 'TargetPrem', label: 'Target premium', type: 'currency'},
            {id: 'InitialDB', label: 'Initial death benefit', type: 'currency'},
            {id: 'LapseYear', label: 'Lapse year', type: 'year'},
            {id: 'CashBenefit', label: 'Net surrender value', type: 'currency'},
            {id: 'DeathBenefit', label: 'Total policy amount', type: 'currency'},
            {id: 'AnnualPremium', label: 'Annual premium', type: 'currency'},
            {id: 'MECLimit', label: 'MEC Limit', type: 'currency'},
        ];

        const outList = [] as OutListItem[];

        out.Keyword.forEach((x: any) => {
            let name = x.$.id;
            const index = name.indexOf('_');
            name = name.slice(index + 1);

            if (x.Value) {
                let value = x.Value[0];
                if (x.Value.length > 1) {
                    value = x.Value.map((z: any) => parseFloat(z));
                }

                const outP = outParams.find((x) => x.id == name);
                if (outP == null) {
                    return null;
                }

                name = outP.label;
                console.log(outP.id);

                const pushData: any = {name, value, type: outP.type};

                if (name === 'Initial death benefit') {
                    pushData.fieldName = TemplateDataFields.faceAmount;
                }
                if (name === 'Product name') {
                    pushData.fieldName = TemplateDataFields.productName;
                }
                if (
                    name === 'Initial annual premium' &&
                    ['LSWFlexLife', 'LSWFlexLifeNL'].includes(productIUL) &&
                    premium === '7PAY'
                ) {
                    // Needed so that terminology is consistent for monthly and annual premium
                    pushData.name = 'Max annual premium';
                }
                if (name === 'Carrier') {
                    pushData.fieldName = TemplateDataFields.carrier;
                }
                outList.push(pushData);

                if (outP.id == 'InitialAnnPrem') {
                    outList.push({
                        name:
                            ['LSWFlexLife', 'LSWFlexLifeNL'].includes(productIUL) && premium === '7PAY'
                                ? 'Max monthly premium'
                                : 'Initial monthly premium',
                        value: parseFloat(value) / 12,
                        type: 'currency',
                        fieldName: TemplateDataFields.monthlyPremium,
                    });
                } else if (outP.id === 'TargetPrem') {
                    outList.push({
                        name: 'Target monthly premium',
                        value: parseFloat(value) / 12,
                        type: 'currency',
                        fieldName: TemplateDataFields.monthlyPremium,
                    });
                } else if (outP.id === 'AnnualPremium') {
                    outList.push({
                        name: 'Annual premium',
                        value: parseFloat(value),
                        type: 'currency',
                        fieldName: TemplateDataFields.monthlyPremium,
                    });
                    outList.push({
                        name: 'Annual monthly premium',
                        value: parseFloat(value) / 12,
                        type: 'currency',
                        fieldName: TemplateDataFields.monthlyPremium,
                    });
                } else if (outP.id == 'CashBenefit') {
                    outList.push({name: 'Cash value at age 60', value: value[60 - age - 1], type: 'currency'});
                    outList.push({name: 'Cash value at age 65', value: value[65 - age - 1], type: 'currency'});
                    outList.push({
                        name: 'Cash value at age 70',
                        value: value[70 - age - 1],
                        type: 'currency',
                        fieldName: TemplateDataFields.cashValue,
                    });
                }
            }
        });
        outList.push({name: 'Illustration', value: out.SourceLedger?.[0]?.URL?.[0], type: 'string'});

        const faceAmount = outList.find((x) => x.name == 'Initial death benefit')?.value;
        const monthlyPremium = outList.find((x) => x.name == 'Initial monthly premium')?.value;
        let productName: any = outList.find((x) => x.name === 'Product name')?.value;

        return {
            faceAmount,
            monthlyPremium,
            productName,
            req: JSON.stringify(args), // Include the request details
            changes,
            out: outList,
            requestDetails: args,
        };
    }
};
